/*
 * A placeholder for content that has not arrived.
 *
 * It is `aria-hidden`, always, and that is the whole design of it. A skeleton
 * is a drawing of a shape, and read aloud it is either nothing or a stream of
 * meaningless boxes. What a screen reader needs instead is for the region to
 * say it is busy and then say what arrived, which is the caller's job:
 * `aria-busy` on the region, and a `role="status"` that announces the result.
 * A skeleton that quietly does neither is the reason a loading table sounds
 * like an empty one.
 *
 * The shimmer is suppressed under reduced motion by base.css, and nothing is
 * lost when it is: the block is still visibly a placeholder because of where it
 * is and what shape it is, not because it is moving.
 */

import type { CSSProperties, HTMLAttributes } from 'react'
import styles from './Skeleton.module.css'

export interface SkeletonProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children' | 'style'> {
  /** Any CSS length. Defaults to filling its container. */
  width?: string
  /** Any CSS length. Defaults to one line of body text. */
  height?: string
  /** Rounds it fully, for an avatar placeholder. */
  circle?: boolean
  /**
   * Stacks this many lines, the last one short, which is what a paragraph
   * actually looks like. One full-width bar per line reads as a table.
   */
  lines?: number
}

export function Skeleton({ width, height, circle = false, lines = 1, className, ...rest }: SkeletonProps) {
  /*
   * The one inline style this codebase allows: a runtime value crossing into
   * CSS as a custom property. React drops an undefined value, so an unset width
   * falls through to the default declared in the stylesheet rather than being
   * written as the string "undefined".
   */
  if (lines > 1) {
    return (
      <span
        {...rest}
        className={[styles.stack, className].filter(Boolean).join(' ')}
        style={{ '--skeleton-w': width, '--skeleton-h': height } as CSSProperties}
        aria-hidden="true"
      >
        {Array.from({ length: lines }, (_unused, index) => (
          <span
            key={index}
            className={[styles.skeleton, index === lines - 1 ? styles.lastLine : '']
              .filter(Boolean)
              .join(' ')}
          />
        ))}
      </span>
    )
  }

  return (
    <span
      {...rest}
      className={[styles.skeleton, circle ? styles.circle : '', className].filter(Boolean).join(' ')}
      style={{ '--skeleton-w': width, '--skeleton-h': height } as CSSProperties}
      aria-hidden="true"
    />
  )
}
